// ── Hex/HSV color conversion helpers ─────────────────────────────────────────
//
// Used by ColorPickerButton to drive its saturation/brightness pad and hue slider
// from (and back into) the plain hex strings the rest of the app stores branding
// colors as.

/** True for a 3- or 6-digit hex color (with or without leading `#`). */
export const HEX_PATTERN = /^#?[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/

/** Normalizes a 3- or 6-digit hex string to lowercase 6-digit `#rrggbb`, or `null` if invalid. */
export function normalizeHex(value: string): string | null {
  if (!HEX_PATTERN.test(value)) return null
  const hex = value.startsWith('#') ? value : `#${value}`
  if (hex.length === 4) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`.toLowerCase()
  }
  return hex.toLowerCase()
}

/** A color in hue/saturation/brightness form. */
export interface Hsv {
  /** Hue in degrees, `0`-`360`. */
  h: number
  /** Saturation, `0`-`1`. */
  s: number
  /** Brightness/value, `0`-`1`. */
  v: number
}

/** A color as 0-255 red/green/blue channels. */
export interface Rgb {
  r: number
  g: number
  b: number
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n))
}

/** Parses a normalized 6-digit `#rrggbb` hex string into 0-255 RGB channels. */
export function hexToRgb(hex: string): Rgb {
  const n = parseInt(hex.slice(1), 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}

/** Converts 0-255 RGB channels (out-of-range/fractional values are clamped and rounded) to a 6-digit lowercase hex string. */
export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (c: number) => clamp(Math.round(c), 0, 255).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/** Converts any valid (3- or 6-digit) hex string to HSV, or `null` if invalid. */
export function hexToHsv(value: string): Hsv | null {
  const hex = normalizeHex(value)
  if (!hex) return null
  const { r, g, b } = hexToRgb(hex)
  const rN = r / 255
  const gN = g / 255
  const bN = b / 255
  const max = Math.max(rN, gN, bN)
  const min = Math.min(rN, gN, bN)
  const delta = max - min

  let h = 0
  if (delta !== 0) {
    if (max === rN) h = 60 * (((gN - bN) / delta) % 6)
    else if (max === gN) h = 60 * ((bN - rN) / delta + 2)
    else h = 60 * ((rN - gN) / delta + 4)
  }
  if (h < 0) h += 360

  const s = max === 0 ? 0 : delta / max
  const v = max
  return { h, s, v }
}

/** Converts HSV back to a 6-digit lowercase hex string. */
export function hsvToHex({ h, s, v }: Hsv): string {
  const c = v * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = v - c

  let rgb: [number, number, number]
  if (h < 60) rgb = [c, x, 0]
  else if (h < 120) rgb = [x, c, 0]
  else if (h < 180) rgb = [0, c, x]
  else if (h < 240) rgb = [0, x, c]
  else if (h < 300) rgb = [x, 0, c]
  else rgb = [c, 0, x]

  const [r, g, b] = rgb
  return rgbToHex((r + m) * 255, (g + m) * 255, (b + m) * 255)
}
